import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Card,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  Textarea,
  Typography,
} from "@mui/joy";
import { apiClient } from "../../lib/api";
import {
  formatMoney,
  mailCampaignsApi,
  mailRatesApi,
  directMailSettingsApi,
} from "../../lib/directMailApi";
import { getApiErrorMessage } from "../../lib/apiError";
import {
  MAIL_TEMPLATE_LABELS,
  type MailCampaign,
  type MailCampaignPayload,
  type MailCampaignStatus,
  type MailTemplateKind,
  type Paginated,
} from "../../types/directMail";
import { loadAllPages } from "./loadAllPages";
import { MailLayoutPicker } from "./MailLayoutPicker";
import { ROOF_QUOTE_SETUP_PATH, useRoofQuoteWidget } from "./roofQuotePricing";

const TEMPLATE_RATE_KEYS: Record<MailTemplateKind, string> = {
  packet: "packet_page",
  postcard: "postcard_6x9",
};

const TEMPLATE_BLURBS: Record<MailTemplateKind, string> = {
  packet:
    "A multi-page estimate: satellite photo of their roof and the price on the cover, an itemised estimate, your credentials, financing, and the QR code. Costs more per piece and reads like a real proposal.",
  postcard:
    "A 6x9 card, front and back: the house, the price, one offer line and the QR code. Cheapest way to get a real number in front of a whole neighborhood.",
};

interface NamedRecord {
  id: number;
  name: string;
}

/**
 * A picker of everything the org has, not the first twenty of it: these
 * endpoints run DRF's plain pagination, which ignores `page_size`, so a single
 * request would silently drop the QR code or source the user is looking for.
 */
function useNamedList(queryKey: string, path: string) {
  return useQuery({
    queryKey: ["direct-mail", "picker", queryKey],
    queryFn: () =>
      loadAllPages<NamedRecord>(async (page) => {
        const response = await apiClient.get<
          NamedRecord[] | Partial<Paginated<NamedRecord>>
        >(`${path}?page=${page}`);
        const data = response.data;
        if (Array.isArray(data)) {
          return {
            count: data.length,
            next: null,
            previous: null,
            results: data,
          };
        }
        return {
          count: data?.count ?? 0,
          next: data?.next ?? null,
          previous: data?.previous ?? null,
          results: data?.results ?? [],
        };
      }),
    staleTime: 5 * 60 * 1000,
  });
}

function emptyForm(): MailCampaignPayload {
  return {
    name: "",
    service_area: "",
    layout_preset: "classic",
    status: "active",
    template_kind: "packet",
    quote_tier: null,
    qr_code: null,
    contact_source: null,
    headline: "Your roof estimate is ready",
    offer_text: "",
    disclaimer_text:
      "This estimate is based on satellite measurements of your roof. Final pricing is confirmed after an on-site inspection.",
    show_financing: true,
    financing_months: 120,
    financing_apr: "9.99",
  };
}

function formFromCampaign(campaign: MailCampaign): MailCampaignPayload {
  return {
    name: campaign.name,
    service_area: campaign.service_area ?? "",
    layout_preset: campaign.layout_preset ?? "classic",
    status: campaign.status,
    template_kind: campaign.template_kind,
    quote_tier: campaign.quote_tier,
    qr_code: campaign.qr_code,
    contact_source: campaign.contact_source,
    headline: campaign.headline,
    offer_text: campaign.offer_text,
    disclaimer_text: campaign.disclaimer_text,
    show_financing: campaign.show_financing,
    financing_months: campaign.financing_months,
    financing_apr: campaign.financing_apr,
  };
}

export interface CampaignFormModalProps {
  open: boolean;
  campaign: MailCampaign | null;
  onClose: () => void;
  onSaved: (campaign: MailCampaign, created: boolean) => void;
}

export const CampaignFormModal: React.FC<CampaignFormModalProps> = ({
  open,
  campaign,
  onClose,
  onSaved,
}) => {
  const [form, setForm] = useState<MailCampaignPayload>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const widgetQuery = useRoofQuoteWidget();
  const settingsQuery = useQuery({
    queryKey: ["direct-mail", "settings"],
    queryFn: () => directMailSettingsApi.get(),
    enabled: open,
  });
  const ratesQuery = useQuery({
    queryKey: ["direct-mail", "rates"],
    queryFn: () => mailRatesApi.list(),
    staleTime: 5 * 60 * 1000,
  });
  const qrCodesQuery = useNamedList("qr-codes", "/api/marketing/qr-codes/");
  const sourcesQuery = useNamedList(
    "contact-sources",
    "/api/crm/contact-sources/",
  );

  useEffect(() => {
    if (!open) return;
    setForm(campaign ? formFromCampaign(campaign) : emptyForm());
    setError(null);
    setStep(0);
  }, [open, campaign]);

  const tiers = widgetQuery.data?.tiers ?? [];
  const update = <K extends keyof MailCampaignPayload>(
    key: K,
    value: MailCampaignPayload[K],
  ) => setForm((current) => ({ ...current, [key]: value }));

  const rateFor = (kind: MailTemplateKind) =>
    ratesQuery.data?.find((rate) => rate.key === TEMPLATE_RATE_KEYS[kind]);

  const submit = async () => {
    if (!form.name.trim()) {
      setError("Give the campaign a name so you can tell batches apart later.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const saved = campaign
        ? await mailCampaignsApi.update(campaign.id, form)
        : await mailCampaignsApi.create(form);
      onSaved(saved, campaign === null);
    } catch (err) {
      setError(getApiErrorMessage(err, "That campaign could not be saved."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog
        layout="center"
        sx={{
          width: { xs: "94vw", md: 680 },
          maxHeight: "92vh",
          overflow: "hidden",
        }}
      >
        <ModalClose />
        <DialogTitle>{campaign ? "Edit campaign" : "New campaign"}</DialogTitle>
        <DialogContent sx={{ overflowY: "auto" }}>
          <Stack spacing={2} sx={{ pt: 1, pb: 1 }}>
            {error && (
              <Alert color="danger" variant="soft">
                {error}
              </Alert>
            )}

            <Stack direction="row" flexWrap="wrap" gap={1}>
              {["Format & area", "Layout & copy", "Options", "Review"].map(
                (label, index) => (
                  <Button
                    key={label}
                    size="sm"
                    variant={step === index ? "solid" : "soft"}
                    onClick={() => setStep(index)}
                  >
                    {index + 1}. {label}
                  </Button>
                ),
              )}
            </Stack>
            <Stack spacing={2} sx={{ display: step === 0 ? "flex" : "none" }}>
              <FormControl>
                <FormLabel>Service area</FormLabel>
                <Input
                  value={form.service_area ?? ""}
                  onChange={(event) =>
                    update("service_area", event.target.value)
                  }
                  placeholder="Neighborhood or city for this campaign"
                />
                <FormHelperText>
                  Planning label; pin selection determines the actual mailing
                  area.
                </FormHelperText>
              </FormControl>
              <FormControl>
                <FormLabel>Campaign name</FormLabel>
                <Input
                  autoFocus
                  value={form.name}
                  onChange={(event) => update("name", event.target.value)}
                  placeholder="Spring hail — Kettering"
                />
              </FormControl>

              <FormControl>
                <FormLabel>What gets mailed</FormLabel>
                <RadioGroup
                  value={form.template_kind ?? "packet"}
                  onChange={(event) =>
                    update(
                      "template_kind",
                      event.target.value as MailTemplateKind,
                    )
                  }
                >
                  <Stack spacing={1}>
                    {(
                      Object.keys(MAIL_TEMPLATE_LABELS) as MailTemplateKind[]
                    ).map((kind) => {
                      const rate = rateFor(kind);
                      return (
                        <Card
                          key={kind}
                          variant={
                            form.template_kind === kind ? "soft" : "outlined"
                          }
                          color={
                            form.template_kind === kind ? "primary" : "neutral"
                          }
                          sx={{ p: 1.5 }}
                        >
                          <Radio
                            value={kind}
                            label={
                              <Box>
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="baseline"
                                >
                                  <Typography level="title-sm">
                                    {MAIL_TEMPLATE_LABELS[kind]}
                                  </Typography>
                                  {rate && (
                                    <Typography
                                      level="body-xs"
                                      sx={{ color: "text.tertiary" }}
                                    >
                                      {formatMoney(
                                        kind === "packet"
                                          ? settingsQuery.data?.price_per_piece
                                          : rate.price,
                                      )}{" "}
                                      per piece{" "}
                                      {kind === "packet"
                                        ? `(${settingsQuery.data?.page_count ?? "…"} pages)`
                                        : ""}
                                    </Typography>
                                  )}
                                </Stack>
                                <Typography
                                  level="body-xs"
                                  sx={{ color: "text.tertiary", mt: 0.5 }}
                                >
                                  {TEMPLATE_BLURBS[kind]}
                                </Typography>
                              </Box>
                            }
                          />
                        </Card>
                      );
                    })}
                  </Stack>
                </RadioGroup>
              </FormControl>

              <FormControl>
                <FormLabel>Pricing tier</FormLabel>
                {tiers.length === 0 ? (
                  <Alert color="warning" variant="soft">
                    <Box>
                      <Typography level="title-sm">
                        No Roof Quote tiers configured
                      </Typography>
                      <Typography level="body-sm">
                        Every printed price comes from a Roof Quote Widget tier.
                        Until one exists with a price per square, pins in this
                        campaign cannot be priced or mailed.
                      </Typography>
                      <Button
                        size="sm"
                        variant="plain"
                        component={RouterLink}
                        to={ROOF_QUOTE_SETUP_PATH}
                        sx={{ px: 0, mt: 0.5 }}
                      >
                        Set up Roof Quote pricing
                      </Button>
                    </Box>
                  </Alert>
                ) : (
                  <>
                    <Select
                      value={form.quote_tier ?? null}
                      onChange={(_, value) => update("quote_tier", value)}
                      slotProps={{ button: { "aria-label": "Pricing tier" } }}
                    >
                      <Option value={null}>
                        Use the default tier (Better)
                      </Option>
                      {tiers.map((tier) => (
                        <Option key={tier.id} value={tier.id}>
                          {tier.name} —{" "}
                          {formatMoney(tier.base_price_per_square, {
                            decimals: 0,
                          })}{" "}
                          per square
                        </Option>
                      ))}
                    </Select>
                    <FormHelperText>
                      The tier the printed price is calculated from. Change it
                      under Marketing → Roof Quote Widget.
                    </FormHelperText>
                  </>
                )}
              </FormControl>
            </Stack>
            <Stack spacing={2} sx={{ display: step === 1 ? "flex" : "none" }}>
              <MailLayoutPicker
                value={form.layout_preset ?? "classic"}
                onChange={(value) => update("layout_preset", value)}
              />
              <Divider />

              <FormControl>
                <FormLabel>Headline</FormLabel>
                <Input
                  value={form.headline ?? ""}
                  onChange={(event) => update("headline", event.target.value)}
                  placeholder="Your roof estimate is ready"
                  slotProps={{ input: { maxLength: 200 } }}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Offer</FormLabel>
                <Textarea
                  minRows={2}
                  value={form.offer_text ?? ""}
                  onChange={(event) => update("offer_text", event.target.value)}
                  placeholder="Optional. Printed under the price — a seasonal promotion, a free inspection, a deadline."
                />
              </FormControl>

              <FormControl>
                <FormLabel>Disclaimer</FormLabel>
                <Textarea
                  minRows={2}
                  value={form.disclaimer_text ?? ""}
                  onChange={(event) =>
                    update("disclaimer_text", event.target.value)
                  }
                />
                <FormHelperText>
                  Printed in small type next to the price. Keep it honest about
                  what a satellite estimate is.
                </FormHelperText>
              </FormControl>

              <Divider />
            </Stack>
            <Stack spacing={2} sx={{ display: step === 2 ? "flex" : "none" }}>
              <FormControl
                orientation="horizontal"
                sx={{ justifyContent: "space-between" }}
              >
                <Box>
                  <FormLabel>Show a monthly payment</FormLabel>
                  <FormHelperText sx={{ mt: 0 }}>
                    Prints an estimated payment alongside the total.
                  </FormHelperText>
                </Box>
                <Switch
                  checked={Boolean(form.show_financing)}
                  onChange={(event) =>
                    update("show_financing", event.target.checked)
                  }
                />
              </FormControl>

              {form.show_financing && (
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <FormControl sx={{ flex: 1 }}>
                    <FormLabel>Term</FormLabel>
                    <Input
                      type="number"
                      value={form.financing_months ?? 120}
                      onChange={(event) =>
                        update("financing_months", Number(event.target.value))
                      }
                      endDecorator="months"
                      slotProps={{ input: { min: 1, max: 360 } }}
                    />
                  </FormControl>
                  <FormControl sx={{ flex: 1 }}>
                    <FormLabel>APR</FormLabel>
                    <Input
                      type="number"
                      value={form.financing_apr ?? ""}
                      onChange={(event) =>
                        update("financing_apr", event.target.value)
                      }
                      endDecorator="%"
                      slotProps={{ input: { min: 0, max: 100, step: 0.01 } }}
                    />
                  </FormControl>
                </Stack>
              )}

              <Divider />

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <FormControl sx={{ flex: 1 }}>
                  <FormLabel>Lead source</FormLabel>
                  <Select
                    value={form.contact_source ?? null}
                    onChange={(_, value) => update("contact_source", value)}
                    slotProps={{ button: { "aria-label": "Lead source" } }}
                  >
                    <Option value={null}>Default "Direct Mail" source</Option>
                    {(sourcesQuery.data ?? []).map((source) => (
                      <Option key={source.id} value={source.id}>
                        {source.name}
                      </Option>
                    ))}
                  </Select>
                  <FormHelperText>
                    Stamped on every lead this campaign produces.
                  </FormHelperText>
                </FormControl>

                <FormControl sx={{ flex: 1 }}>
                  <FormLabel>QR code</FormLabel>
                  <Select
                    value={form.qr_code ?? null}
                    onChange={(_, value) => update("qr_code", value)}
                    slotProps={{ button: { "aria-label": "QR code" } }}
                  >
                    <Option value={null}>Generate one per piece</Option>
                    {(qrCodesQuery.data ?? []).map((code) => (
                      <Option key={code.id} value={code.id}>
                        {code.name}
                      </Option>
                    ))}
                  </Select>
                  <FormHelperText>
                    Optional branded QR from Marketing → QR Codes.
                  </FormHelperText>
                </FormControl>
              </Stack>

              <FormControl>
                <FormLabel>Status</FormLabel>
                <Select
                  value={form.status ?? "active"}
                  onChange={(_, value) =>
                    value && update("status", value as MailCampaignStatus)
                  }
                  slotProps={{ button: { "aria-label": "Campaign status" } }}
                >
                  <Option value="draft">
                    Draft — not offered when pinning
                  </Option>
                  <Option value="active">Active</Option>
                  <Option value="archived">Archived</Option>
                </Select>
              </FormControl>
            </Stack>
            {step === 3 && (
              <Stack spacing={2}>
                <Typography level="h4">
                  Ready to create your campaign?
                </Typography>
                <Typography>
                  {form.name || "Name required"} ·{" "}
                  {form.service_area || "No area label"}
                </Typography>
                <Typography>
                  {form.template_kind} · {form.layout_preset} layout ·{" "}
                  {tiers.find((t) => t.id === form.quote_tier)?.name ||
                    "Default Better tier"}
                </Typography>
                <Typography level="title-md">{form.headline}</Typography>
                <Typography>
                  {form.offer_text || "No additional offer"}
                </Typography>
                <Typography level="body-sm">{form.disclaimer_text}</Typography>
                {tiers.length === 0 && (
                  <Alert color="warning">
                    Set up a priced Roof Quote tier before measuring or mailing.
                  </Alert>
                )}
                <Alert>
                  Creating this campaign is free. The batch cost planner shows
                  included pages, excluded houses, and the full mailing price
                  before proof approval and payment.
                </Alert>
              </Stack>
            )}
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button variant="plain" color="neutral" onClick={onClose}>
                Cancel
              </Button>
              {step > 0 && (
                <Button variant="outlined" onClick={() => setStep(step - 1)}>
                  Back
                </Button>
              )}
              {step < 3 ? (
                <Button onClick={() => setStep(step + 1)}>Continue</Button>
              ) : (
                <Button
                  loading={saving}
                  disabled={!form.name.trim()}
                  onClick={() => void submit()}
                >
                  {campaign ? "Save changes" : "Create campaign"}
                </Button>
              )}
            </Stack>
          </Stack>
        </DialogContent>
      </ModalDialog>
    </Modal>
  );
};
